// =============================================================================
// ui/auth/ResetPassword.tsx — set a new password, then sign out everywhere.
// -----------------------------------------------------------------------------
// 🚨 A SCREEN, NOT A MODAL, AND THAT IS THE WHOLE DESIGN.
//
// Beta drew this line in the UI: it booted the app and layered a modal on top.
// Every path that skipped the modal — a refresh, most obviously — skipped the
// intent with it, and the user ended up browsing with the old password still
// live (#31). Here "recovery pending" is one of the auth gate's three states, so
// there is no path that reaches the app without passing through this. Nothing
// else renders, no data loads.
//
// 🔴 THE SECURITY IS IN THE SIGN-OUT, NOT IN THE GATE. See recovery.ts: a
// client-side gate cannot enforce anything against someone holding the token.
// What this screen does that matters is invalidate every OTHER session when the
// password changes — OWASP's rule that a credential change must invalidate
// sessions established with the old credential.
//
// ⚠ Without it, "reset my password" does not evict an intruder who is already
// signed in. That is the difference between a reset that is a remedy and one
// that is a formality, and neither client did it before this.
// =============================================================================

import { useState, type FormEvent } from 'react';
import { supabase } from '../../io/supabase/client';
import {
  MIN_PASSWORD_LENGTH, clearRecoveryPending, passwordProblem,
} from './recovery';
import { reportFailure, reportFailureAs } from '../../io/diagnostics/errorLog';

export function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const problem = passwordProblem(password, confirm);
    if (problem) { setError(problem); return; }

    setBusy(true);
    setError(null);
    try {
      const { error: err } = await supabase.auth.updateUser({ password });
      if (err) {
        // ⚠ Supabase's own message, not a generic one. "Password should be at
        // least N characters" and "New password should be different from the
        // old password" are different problems with different fixes, and
        // leaked-password rejection (if the project has it enabled) arrives
        // here too — collapsing them would hide the most useful one.
        //
        // ⚠ `reportFailureAs`, not `reportFailure`, so both the screen and the log
        // read `.message` whatever shape comes back.
        //
        // 🔴 THE REASON THIS USED TO GIVE WAS FALSE. It said Supabase "returns
        // its own error shape rather than throwing, and a generic reader logs
        // `[object Object]`". Checked against the INSTALLED library 2026-08-27:
        // `AuthError extends Error` (auth-js/.../errors.js:22), so
        // `reportFailure` would have returned `e.message` correctly. The belief
        // came from THIS REPO'S TEST DOUBLES, which resolve bare
        // `{ message: '…' }` objects. Pinning is still right — it is correct
        // under both, and the doubles are what the suite exercises — but it is
        // belt-and-braces, not a rescue. See #52.
        setError(reportFailureAs('password change', err, err.message));
        return;
      }

      // 🚨 CLEARED ONLY NOW. Clearing on mount or on submit would let a FAILED
      // update drop the gate and leave a live recovery session browsing the
      // app — the exact state this whole flow exists to prevent.
      clearRecoveryPending();

      // 🚨 EVERY OTHER SESSION, INVALIDATED. The reason is above; the effect is
      // that anyone signed in with the old password is now out, including this
      // recovery session once the local sign-out lands.
      //
      // ⚠ 'global' rather than 'others', deliberately. Signing out here too
      // sends the user back to the sign-in form to type the password they just
      // chose, which confirms it works and confirms it is stored. Beta reloaded
      // them straight into the app, which is friendlier and leaves the recovery
      // session alive for its full lifetime.
      //
      // ⚠ A failure here is NOT surfaced as an error: the password HAS changed,
      // and telling the user it failed would send them to reset it again. It is
      // logged instead, and the local sign-out below still runs.
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch (signOutErr) {
        console.error('Password changed, but other sessions were not signed out:', signOutErr);
        try { await supabase.auth.signOut(); } catch { /* the gate is already cleared */ }
      }
      // No success branch: onAuthStateChange in App swaps the tree back to
      // SignIn as soon as the session is gone.
    } catch (err) {
      setError(reportFailure('password change', err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card" onSubmit={onSubmit}>
      <h3>Choose a new password</h3>
      <p className="muted">
        You followed a reset link, so you are part-way through changing your
        password. Setting it finishes the job — and signs out anything else that
        is currently using this account.
      </p>

      <label htmlFor="rp-new">New password</label>
      <input
        id="rp-new" type="password" autoComplete="new-password"
        value={password} onChange={(e) => setPassword(e.target.value)}
        disabled={busy}
      />

      <label htmlFor="rp-confirm">Confirm new password</label>
      <input
        id="rp-confirm" type="password" autoComplete="new-password"
        value={confirm} onChange={(e) => setConfirm(e.target.value)}
        disabled={busy}
      />

      {/* ⚠ Says WHY, not just what. "At least 12 characters" reads as an
          arbitrary hoop; the reason is the thing that changes behaviour. */}
      <p className="muted">
        At least {MIN_PASSWORD_LENGTH} characters. No symbols or digits required —
        length is what makes a password hard to guess, so a phrase you can
        remember beats a short one with punctuation in it.
      </p>

      {error ? <p className="red">{error}</p> : null}

      <button type="submit" className="btn primary" disabled={busy}>
        {busy ? 'Saving…' : 'Set password and sign out everywhere'}
      </button>

      {/* 🚨 NO CANCEL, and its absence is deliberate. A cancel that signs you
          out is a trap door for someone who just wants to read the screen
          again, and a cancel that lets you into the app is the beta bug. If the
          user does not want to change their password, closing the tab ends the
          recovery session's usefulness for this browser. */}
    </form>
  );
}
